import React from 'react'
import { Badge, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GroupsIcon from '@mui/icons-material/Groups'
import EventNoteIcon from '@mui/icons-material/EventNote'
import PersonIcon from '@mui/icons-material/Person'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined'
import { MenuListItem } from '../styles/MainMenu.styles'
import {
  MAIN_MENU_NAV_ITEMS_WHEN_SUSPENDED,
  isAvailableWhenCompanySuspended,
  suspendedCompanyNavTooltip,
  type SuspendedCompanyNavItemId,
} from './utils/suspendedCompanyGate'

const ICONS: Record<SuspendedCompanyNavItemId, React.ReactElement> = {
  publicLobby: <DashboardIcon />,
  myRooms: <GroupsIcon />,
  meetings: <EventNoteIcon />,
  profile: <PersonIcon />,
  whatsNew: <NewReleasesIcon />,
  feedback: <FeedbackOutlined />,
}

/** Sidebar actions that open a drawer rather than a section. */
const ITEM_EVENTS: Partial<Record<SuspendedCompanyNavItemId, string>> = {
  whatsNew: 'openProductUpdates',
  feedback: 'openFeedbackDrawer',
}

export interface SuspendedCompanyNavProps {
  activeSection: string
  companyName: string | null
  onSectionChange: (section: string) => void
  productUpdatesUnread?: boolean
  tooltipPlacement?: 'right' | 'bottom'
}

/**
 * The MainMenu items while the signed-in user's company is suspended (SC-409). Items on
 * `SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED` work as usual; the others are greyed out, not
 * clickable and explain why. Items not in `MAIN_MENU_NAV_ITEMS_WHEN_SUSPENDED` are not shown.
 */
export default function SuspendedCompanyNav({
  activeSection,
  companyName,
  onSectionChange,
  productUpdatesUnread = false,
  tooltipPlacement = 'right',
}: SuspendedCompanyNavProps) {
  const tooltip = suspendedCompanyNavTooltip(companyName)

  return (
    <>
      {MAIN_MENU_NAV_ITEMS_WHEN_SUSPENDED.map(({ id, label }) => {
        const icon =
          id === 'whatsNew' ? (
            <Badge
              color="error"
              variant="dot"
              invisible={!productUpdatesUnread}
              sx={{ '& .MuiBadge-badge': { top: 6, right: 6 } }}
            >
              {ICONS[id]}
            </Badge>
          ) : (
            ICONS[id]
          )
        const isSection = !ITEM_EVENTS[id]

        if (isAvailableWhenCompanySuspended(id)) {
          const onClick = () => {
            const eventName = ITEM_EVENTS[id]
            if (eventName) window.dispatchEvent(new CustomEvent(eventName))
            else onSectionChange(id)
          }
          return (
            <MenuListItem
              key={id}
              data-suspended-nav-item={id}
              {...(isSection ? { 'data-mainmenu-section': id, tabIndex: -1 } : {})}
              $active={isSection && activeSection === id}
              onClick={onClick}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={label} />
            </MenuListItem>
          )
        }

        return (
          <Tooltip key={id} title={tooltip} placement={tooltipPlacement}>
            <span>
              <MenuListItem
                data-suspended-nav-item={id}
                aria-disabled="true"
                $active={false}
                sx={{
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} />
              </MenuListItem>
            </span>
          </Tooltip>
        )
      })}
    </>
  )
}
